import '@radical/styles'
// 被动滚动优化样式
import './styles/passive-scroll.css'
// svg
import 'virtual:svg-icons-register'
import App from './app.vue'
import { createApp, watch } from 'vue'
import { InitRouter } from '@radical/router'
import { setupRouteGuard } from '@/router/guard'
import { setupI18n, getLocale } from '@radical/locale'
import { setupFormilyI18n, changeFormilyLocale } from '@radical/formily'
import { setupPinia } from '@radical/stores'
import { initApplication, initHttpRequest } from './init-application'
import { BasicRoutes } from './router/routes'
import { setupTable, VXETable, XEUtils, zhCN, enUS } from '@radical/table'
import ganttastic from '@infectoone/vue-ganttastic'
import { initScrollOptimization } from './utils/scroll-optimizer'
import { getSysInfo } from '@/apis/business/global'

async function filterRoutes(retryCount = 0, maxRetries = 3) {
  try {
    const sysInfo = await getSysInfo()
    const plugins = sysInfo.plugins.filter((plugin) => !plugin.disabled)
    console.log('System plugins loaded:', plugins)
    BasicRoutes.forEach((route) => {
      switch (route.name) {
        case 'Cluster':
          if (!plugins.some((plugin) => plugin.name === 'Cluster')) {
            route.meta!.hideMenu = true
          }
          break
        case 'Mix':
          if (!plugins.some((plugin) => plugin.name === 'Mix')) {
            route.meta!.hideMenu = true
          }
          break
        case 'Device':
          if (!plugins.some((plugin) => plugin.name === 'GB28181')) {
            route.meta!.hideMenu = true
          }
          break
        case 'Stress':
        case 'Test':
          if (!plugins.some((plugin) => plugin.name === 'Test')) {
            route.meta!.hideMenu = true
          }
          break
        case 'Monitor':
          route.children?.forEach((child) => {
            switch (child.name) {
              case 'Heap':
              case 'Cpu':
              case 'TcpdumpCapture':
                if (!plugins.some((plugin) => plugin.name === 'Debug')) {
                  child.meta!.hideMenu = true
                }
                break
            }
          })
          break
        default:
          break
      }
    })
    return BasicRoutes
  } catch (error) {
    console.error(
      `Failed to load system info (attempt ${retryCount + 1}/${
        maxRetries + 1
      }):`,
      error,
    )

    // 如果达到最大重试次数，返回默认路由
    if (retryCount >= maxRetries) {
      console.warn('Max retries reached, using default routes')
      return BasicRoutes
    }

    // 指数退避策略：1s, 2s, 4s
    const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)

    return new Promise((resolve) => {
      setTimeout(() => {
        filterRoutes(retryCount + 1, maxRetries).then(resolve)
      }, delay)
    })
  }
}

;(async () => {
  // 首先初始化滚动优化
  // initScrollOptimization()

  const app = createApp(App)
  // 初始化pinia
  setupPinia(app)
  // 多语言配置，异步场景：可以从服务器端获取语言文件
  await setupI18n(app)

  // 配置 VXE Table 国际化
  const updateVxeTableLocale = () => {
    const locale = getLocale.value
    const vxeLocale = locale === 'en' ? enUS : zhCN
    VXETable.setup({
      i18n: (key, args) =>
        XEUtils.toFormatString(XEUtils.get(vxeLocale, key), args),
    })
  }
  // 初始化 VXE Table 语言
  updateVxeTableLocale()
  // 监听语言变化
  watch(() => getLocale.value, updateVxeTableLocale)

  // 配置 Formily 国际化
  setupFormilyI18n(getLocale.value)
  // 监听语言变化，同步更新 Formily 语言
  watch(
    () => getLocale.value,
    (newLocale) => {
      changeFormilyLocale(newLocale)
    },
  )

  // 初始化应用配置，需要在router之前，确保可以生成正确的菜单
  await initApplication()

  const routes = await filterRoutes()

  // 初始化 Router
  const router = InitRouter(import.meta.env.VITE_PUBLIC_PATH, routes)
  app.use(router)
  // 路由守卫
  await setupRouteGuard()
  // console.log('Router initialized with routes:', router.getRoutes())
  await router.isReady()
  // console.log('Router ready with routes:', router.getRoutes())
  // http请求初始化需要在router和store之后，否则http内拿不到router对象
  await initHttpRequest()
  // vxe-table
  app.use(setupTable)
  app.use(ganttastic)
  // 挂载app实例
  app.mount('#app')
  // 关闭mock, 支持Tree Shaking `mockjs`
  if (__VITE_USE_MOCK__) {
    import('../mock/_mock-server').then(({ setupProdMockServer }) =>
      setupProdMockServer(),
    )
  }
})()
